import React from 'react';
import { Mail, Shield, Zap, Users, CheckCircle, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const features = [
    {
      icon: Mail,
      title: 'Email Broadcasting',
      description: 'Send mass emails efficiently to students, faculty, and staff across the university'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with guaranteed delivery and tracking'
    },
    {
      icon: Zap,
      title: 'Fast Delivery',
      description: 'Lightning-fast email delivery to thousands of recipients simultaneously'
    },
    {
      icon: Users,
      title: 'Centralized Management',
      description: 'Manage all university communications from a single, unified platform'
    }
  ];

  const benefits = [
    'Official UMS domain authentication (if needed)',
    'Advanced analytics and reporting',
    'Scheduled email campaigns',
    'Recipient list management',
    'Delivery status tracking'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-ums-blue rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-ums-blue">UMSConnect</h1>
                <p className="text-xs text-gray-600">Email Broadcast System</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-ums-blue transition-colors">Features</a>
              <a href="#benefits" className="text-gray-700 hover:text-ums-blue transition-colors">Benefits</a>
              <a href="#contact" className="text-gray-700 hover:text-ums-blue transition-colors">Contact</a>
              <Button className="bg-ums-blue hover:bg-ums-blue-dark text-white" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
                Request Access
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-ums-gold/10 rounded-full mb-6">
              <Shield className="w-4 h-4 text-ums-gold mr-2" />
              <span className="text-sm font-medium text-ums-blue">Official UMS Email Broadcast System</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Streamline University
              <span className="text-ums-blue"> Email Communications</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              UMSConnect is the official email broadcasting platform for Universitas Muhammadiyah Surakarta,
              enabling efficient mass communication with students, faculty, and staff.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-ums-blue hover:bg-ums-blue-dark text-white text-lg px-8 py-6" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
                Request Access
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-ums-blue text-ums-blue hover:bg-ums-blue hover:text-white text-lg px-8 py-6" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-ums-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">99.9%</div>
              <div className="text-ums-gold font-medium">Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">Up to 50K</div>
              <div className="text-ums-gold font-medium">Emails Daily</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">100%</div>
              <div className="text-ums-gold font-medium">Secure</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">24/7</div>
              <div className="text-ums-gold font-medium">Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Powerful Features</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to manage university-wide email communications effectively
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-ums-blue/10 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-7 h-7 text-ums-blue" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Choose UMSConnect?</h2>
              <p className="text-lg text-gray-600 mb-8">
                Built specifically for Universitas Muhammadiyah Surakarta's communication needs,
                UMSConnect provides a robust, scalable solution for all your email broadcasting requirements.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-ums-gold flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-ums-blue to-ums-blue-dark rounded-2xl p-8 shadow-2xl">
                <div className="space-y-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-12 h-12 bg-ums-gold rounded-full flex items-center justify-center">
                        <Mail className="w-6 h-6 text-ums-blue" />
                      </div>
                      <div>
                        <div className="text-white font-semibold">New Campaign</div>
                        <div className="text-white/70 text-sm">Sent to 15,000 recipients</div>
                      </div>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-2">
                      <div className="bg-ums-gold rounded-full h-2" style={{ width: '85%' }}></div>
                    </div>
                    <div className="text-white/90 text-sm mt-2">85% delivery rate</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                    <div className="text-white font-semibold mb-3">Recent Activity</div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-white/80">Academic announcement</span>
                        <span className="text-ums-gold font-medium">Success</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-white/80">Event notification</span>
                        <span className="text-ums-gold font-medium">Success</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-white/80">Registration reminder</span>
                        <span className="text-ums-gold font-medium">Success</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-ums-blue">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Interested in UMSConnect?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us to learn more about how UMSConnect can improve your department's communication efficiency.
          </p>
          <Button size="lg" className="bg-white text-ums-blue hover:bg-gray-100 text-lg px-8 py-6" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
            Get in Touch
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              For inquiries about UMSConnect email broadcast system, please contact us via email
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Email 1 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-ums-blue rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Management</h3>
                <a 
                  href="mailto:satudata@ums.ac.id" 
                  className="text-ums-blue hover:text-ums-blue-dark text-lg font-medium transition-colors"
                >
                  satudata@ums.ac.id
                </a>
                <p className="text-sm text-gray-600 mt-3">For data and system inquiries</p>
              </CardContent>
            </Card>

            {/* Email 2 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-ums-blue rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Rankings Team</h3>
                <a 
                  href="mailto:rangkings@ums.ac.id" 
                  className="text-ums-blue hover:text-ums-blue-dark text-lg font-medium transition-colors"
                >
                  rangkings@ums.ac.id
                </a>
                <p className="text-sm text-gray-600 mt-3">For rankings and metrics inquiries</p>
              </CardContent>
            </Card>
          </div>

          {/* Additional Info */}
          <div className="mt-12 text-center">
            <Card className="border-0 shadow-lg max-w-2xl mx-auto">
              <CardContent className="p-6">
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <Clock className="w-5 h-5 text-ums-gold" />
                  <h4 className="font-semibold text-gray-900">Response Time</h4>
                </div>
                <p className="text-gray-600">We typically respond to emails within 1-2 business days</p>
                <p className="text-sm text-gray-500 mt-2">Monday - Friday, 8:00 AM - 4:00 PM WIB</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-ums-gold rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-ums-blue" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">UMSConnect</h3>
                  <p className="text-sm text-gray-400">Email Broadcast System</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Official email broadcasting platform for Universitas Muhammadiyah Surakarta
              </p>
              <a 
                href="https://www.ums.ac.id" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-gray-400 hover:text-ums-gold transition-colors"
              >
                <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                  <img 
                    src="https://www.ums.ac.id/wp-content/uploads/2022/05/logo-UMS-1.png" 
                    alt="UMS Logo" 
                    className="w-6 h-6 object-contain"
                  />
                </div>
                <span className="text-sm font-medium">ums.ac.id</span>
              </a>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-gray-400 hover:text-ums-gold transition-colors">Features</a></li>
                <li><a href="#benefits" className="text-gray-400 hover:text-ums-gold transition-colors">Benefits</a></li>
                <li><a href="https://www.ums.ac.id" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-ums-gold transition-colors">UMS Website</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-ums-gold transition-colors">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="mailto:satudata@ums.ac.id" className="hover:text-ums-gold transition-colors">satudata@ums.ac.id</a></li>
                <li><a href="mailto:rangkings@ums.ac.id" className="hover:text-ums-gold transition-colors">rangkings@ums.ac.id</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2025 UMSConnect - Universitas Muhammadiyah Surakarta. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;